import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  SlideContent,
  ButtonsContainer,
  PrimaryButton,
  SecondaryButton,
} from '../shared';

// ─── Error ───────────────────────────────────────────────────────────────────

const ErrorText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #d32f2f;
  margin: 8px 0 0 0;
  text-align: center;
`;

// ─── Page header ─────────────────────────────────────────────────────────────

const PageTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #444444;
  margin: 0 0 4px 0;
`;

const PageSubtitle = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #9e9e9e;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconTile = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: #f5f5f5;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  line-height: 1.5;
`;

const CardSubtitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #9e9e9e;
  line-height: 1.3;
`;

// ─── Week tiles (card 1) ──────────────────────────────────────────────────────

const WeekTilesRow = styled.div`
  display: flex;
  gap: 8px;
`;

const WeekTile = styled.div`
  flex: 1;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WeekTileLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const WeekTileValue = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #444444;
`;

// ─── Calendar columns (card 2) ────────────────────────────────────────────────

const CalendarColumns = styled.div`
  display: flex;
  gap: 12px;
`;

const CalendarColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CalendarColumnLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
`;

const CalendarNameList = styled.div`
  max-height: 120px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CalendarNameItem = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  color: #444444;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px;
  line-height: 1.5;
`;

// ─── Paste date body (card 3) ─────────────────────────────────────────────────

const PasteDateText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #9e9e9e;
  margin: 0;
  line-height: 1.5;
`;

const PasteDateBold = styled.strong`
  color: #444444;
  font-weight: 700;
`;

// ─── Cards container ──────────────────────────────────────────────────────────

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ─── Component ───────────────────────────────────────────────────────────────

const BulkCopySlide4 = ({
  startWeek,
  endWeek,
  copyNotes,
  selectedAccountCalendars,
  selectedAthleteCalendars,
  pasteWeek,
  sourceCalendarName,
  isLoading,
  error,
  onBack,
  onSubmit,
}) => {
  const totalCount = selectedAccountCalendars.length + selectedAthleteCalendars.length;
  const calendarWord = totalCount === 1 ? 'calendar' : 'calendars';
  const hasAccountCalendars = selectedAccountCalendars.length > 0;
  const hasAthleteCalendars = selectedAthleteCalendars.length > 0;

  return (
    <SlideContent>
      <PageTitle>Confirm Copy Details</PageTitle>
      <PageSubtitle>Review the information below before copying planner blocks.</PageSubtitle>

      <CardsContainer>
        {/* Card 1: Copying From */}
        <Card>
          <CardHeader>
            <IconTile>
              <IcomoonReact iconSet={iconSet} size={12} icon='duplicate' color='#616161' />
            </IconTile>
            <CardHeaderText>
              <CardTitle>Copying From</CardTitle>
              <CardSubtitle>{sourceCalendarName || 'Selected calendar'}</CardSubtitle>
            </CardHeaderText>
          </CardHeader>

          <WeekTilesRow>
            <WeekTile>
              <WeekTileLabel>Start Week</WeekTileLabel>
              <WeekTileValue>{startWeek?.label || '—'}</WeekTileValue>
            </WeekTile>
            <WeekTile>
              <WeekTileLabel>End Week</WeekTileLabel>
              <WeekTileValue>{endWeek?.label || '—'}</WeekTileValue>
            </WeekTile>
          </WeekTilesRow>

          <PasteDateText>
            {`Notes ${copyNotes ? 'will' : 'will not'} be copied from the selected date range.`}
          </PasteDateText>
        </Card>

        {/* Card 2: Pasting To */}
        <Card>
          <CardHeader>
            <IconTile>
              <IcomoonReact iconSet={iconSet} size={12} icon='gs-group' color='#616161' />
            </IconTile>
            <CardHeaderText>
              <CardTitle>Pasting To</CardTitle>
              <CardSubtitle>{`${totalCount} ${calendarWord} selected`}</CardSubtitle>
            </CardHeaderText>
          </CardHeader>

          <CalendarColumns>
            {hasAccountCalendars && (
              <CalendarColumn>
                <CalendarColumnLabel>
                  {`Parent Calendars (${selectedAccountCalendars.length})`}
                </CalendarColumnLabel>
                <CalendarNameList>
                  {selectedAccountCalendars.map((cal) => (
                    <CalendarNameItem key={cal.id}>{cal.name}</CalendarNameItem>
                  ))}
                </CalendarNameList>
              </CalendarColumn>
            )}
            {hasAthleteCalendars && (
              <CalendarColumn>
                <CalendarColumnLabel>
                  {`Athlete Calendars (${selectedAthleteCalendars.length})`}
                </CalendarColumnLabel>
                <CalendarNameList>
                  {selectedAthleteCalendars.map((cal) => (
                    <CalendarNameItem key={cal.id}>{`${cal.first} ${cal.last}`}</CalendarNameItem>
                  ))}
                </CalendarNameList>
              </CalendarColumn>
            )}
          </CalendarColumns>
        </Card>

        {/* Card 3: Paste Start Date */}
        <Card>
          <CardHeader>
            <IconTile>
              <IcomoonReact iconSet={iconSet} size={12} icon='calendar2' color='#616161' />
            </IconTile>
            <CardHeaderText>
              <CardTitle>Target Start Date</CardTitle>
            </CardHeaderText>
          </CardHeader>

          <PasteDateText>
            {'Content will be pasted starting '}
            <PasteDateBold>{pasteWeek?.label || '—'}</PasteDateBold>
            {' on target calendars.'}
          </PasteDateText>
        </Card>
      </CardsContainer>

      <ButtonsContainer>
        <PrimaryButton onClick={onSubmit} disabled={isLoading}>
          {isLoading ? 'Copying...' : 'Confirm and Copy'}
        </PrimaryButton>
        <SecondaryButton onClick={onBack} disabled={isLoading}>
          Back
        </SecondaryButton>
      </ButtonsContainer>

      {error && <ErrorText>{error}</ErrorText>}
    </SlideContent>
  );
};

BulkCopySlide4.propTypes = {
  startWeek: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  endWeek: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  copyNotes: PropTypes.bool.isRequired,
  selectedAccountCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })).isRequired,
  selectedAthleteCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
  })).isRequired,
  pasteWeek: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  sourceCalendarName: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

BulkCopySlide4.defaultProps = {
  startWeek: null,
  endWeek: null,
  pasteWeek: null,
  sourceCalendarName: '',
  isLoading: false,
  error: null,
};

export default BulkCopySlide4;
